import { useEffect, type MutableRefObject } from "react";
import { toast } from "react-toastify";
import { OptionTemplate } from "shared/api/middleware-api-client";
import { BeamData } from "./DoorOptionsProvider.types";
import {
  getHandleAnchorBeamGap,
  resolveActiveHandleForPolicy,
} from "./handlePolicy";
import {
  getHandleRepairNotification,
  shouldShowUHandleLengthWarning,
  uHandleLengthWarningNotification,
} from "./handleRepairNotifications";

interface UseHandlePolicyRepairParams {
  beams?: BeamData[];
  currentHandle: OptionTemplate | null;
  doorCode?: string;
  handleOptions?: OptionTemplate[];
  layoutCode?: string;
  pendingHeightRepairNotificationRef: MutableRefObject<boolean>;
  updateHandle: (handle: OptionTemplate) => void;
}

export const useHandlePolicyRepair = ({
  beams,
  currentHandle,
  doorCode,
  handleOptions,
  layoutCode,
  pendingHeightRepairNotificationRef,
  updateHandle,
}: UseHandlePolicyRepairParams) => {
  useEffect(() => {
    if (!handleOptions || !doorCode || !layoutCode) {
      return;
    }

    const anchorBeamGap = getHandleAnchorBeamGap(beams);
    const handleRepair = resolveActiveHandleForPolicy({
      anchorBeamGap,
      doorCode,
      layoutCode,
      currentHandle,
      handleOptions,
    });

    if (handleRepair && handleRepair.nextHandle.code !== currentHandle?.code) {
      updateHandle(handleRepair.nextHandle);
      const handleRepairNotification = getHandleRepairNotification(
        handleRepair.reason,
      );

      if (
        pendingHeightRepairNotificationRef.current &&
        handleRepairNotification
      ) {
        toast.info(handleRepairNotification.message, {
          toastId: handleRepairNotification.toastId,
        });
      }
    } else if (
      pendingHeightRepairNotificationRef.current &&
      shouldShowUHandleLengthWarning({
        anchorBeamGap,
        handleCode: currentHandle?.code,
        layoutCode,
      })
    ) {
      toast.info(uHandleLengthWarningNotification.message, {
        toastId: uHandleLengthWarningNotification.toastId,
      });
    }

    pendingHeightRepairNotificationRef.current = false;
  }, [
    beams,
    currentHandle,
    doorCode,
    handleOptions,
    layoutCode,
    pendingHeightRepairNotificationRef,
    updateHandle,
  ]);
};
